import { Card } from '@/components/ui/card'
import { selectedReferenceFieldSelector } from '@/redux/store/slices/map/mapSlice'
import { referenceFieldColors } from '@/theme'
import { formatValueLegend, referenceFieldStringMap } from '@/utils'
import { useAppSelector } from '@redux/hooks/useAppState'

import type { AggregatedDemographics } from 'honeygrid-types'
import type { DataSourceFieldMappings } from 'honeygrid-types/validators-and-types/DataSourceTypes'
import type { FC } from 'react'

interface MapLegendProps {
	aggregatedDemographics?: AggregatedDemographics
	fieldMappings?: DataSourceFieldMappings
}

export const MapLegend: FC<MapLegendProps> = ({ aggregatedDemographics, fieldMappings }) => {
	const selectedReferenceField = useAppSelector(selectedReferenceFieldSelector)
	const OPACITIES = [0.2, 0.4, 0.6, 0.8, 1]
	const lowerValue = '0'
	const defaultUpperValue = 0

	// Use fieldMappings for custom fields, fallback to referenceFieldStringMap for built-in fields
	const selectedReferenceFieldDisplayValue =
		fieldMappings?.[selectedReferenceField]?.friendlyName ||
		referenceFieldStringMap[selectedReferenceField] ||
		selectedReferenceField

	if (!aggregatedDemographics) {
		return null
	}

	const firstSortedCell: string =
		aggregatedDemographics.sortedCellIds[selectedReferenceField]?.[0] ?? ''
	const maxValueOfField: number =
		aggregatedDemographics.cells[firstSortedCell ?? '']?.[selectedReferenceField] ??
		defaultUpperValue

	const getLegendFillColor = () => {
		if (referenceFieldColors[selectedReferenceField]) {
			return referenceFieldColors[selectedReferenceField].main
		}
		return referenceFieldColors.default.main
	}

	return (
		<Card className="absolute left-3 bottom-18 z-[3] p-3 w-[143px] shadow-md">
			<div className="flex flex-col gap-2">
				<p className="text-sm font-medium truncate" style={{ color: getLegendFillColor() }}>
					{selectedReferenceFieldDisplayValue ?? selectedReferenceField}
				</p>

				<div className="flex flex-col gap-1">
					<div className="flex gap-0.5">
						{OPACITIES.map((opacity, index) => (
							<div
								key={opacity}
								data-testid={`legend-box-${index}`}
								className="h-3 w-[22px] rounded-sm"
								style={{
									backgroundColor: getLegendFillColor(),
									opacity,
								}}
							/>
						))}
					</div>
					<div className="flex justify-between">
						<span className="text-xs text-muted-foreground" data-testid="low-bound-field">
							{lowerValue}
						</span>
						<span className="text-xs text-muted-foreground" data-testid="upper-bound-field">
							{formatValueLegend(maxValueOfField, selectedReferenceField)}
						</span>
					</div>
				</div>
			</div>
		</Card>
	)
}
